import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
  TextField,
  Box,
  OutlinedInputProps,
  ButtonBase,
  Popover,
  InputAdornment,
  Divider,
  createStyles,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { FiChevronDown, FiSearch } from 'react-icons/fi';

import { FieldProps } from '../../types';
import { appTextFieldRootStyle } from '../inputs';
import { getBackground } from '../../theme/styles';
import {
  PhoneCountry,
  applyCountryRules,
  countries,
  detectCountryByDigits,
  dialCodeDigits,
  extractLocalDigits,
  formatDisplay,
  mobileNumberRules,
  splitMask,
  splitPhoneTokens,
  stripNonDigits,
  validatePhoneByCountry,
} from '../../utils/phone';

// Re-exported so the existing deep-import call sites keep working
export { countries, mobileNumberRules } from '../../utils/phone';
export type { PhoneCountry } from '../../utils/phone';

const PRIORITY_COUNT = 10;

const localDigitsBeforeCursor = (
  rawInput: string,
  cursor: number,
  country: PhoneCountry
): number => {
  const before = rawInput.slice(0, cursor);
  const digits = stripNonDigits(before);
  const dial = dialCodeDigits(country);
  if (digits.startsWith(dial)) {
    return digits.length - dial.length;
  }
  return digits.length;
};

const positionAtLocalDigit = (
  formatted: string,
  country: PhoneCountry,
  localTarget: number
): number => {
  if (!formatted) return 0;
  const dial = dialCodeDigits(country);
  const totalTarget = dial.length + Math.max(0, localTarget);
  if (totalTarget <= 0) return splitMask(country).prefix.length;
  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      count++;
      if (count === totalTarget) return i + 1;
    }
  }
  return formatted.length;
};

const useLabelStyles = makeStyles(() => ({
  label: {
    left: '-4px !important',
  },
}));

const useInputStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { ...appTextFieldRootStyle(theme), borderTopLeftRadius: 0, borderBottomLeftRadius: 0 },
    input: {
      padding: '27px 5px 10px',
    },
    focused: {},
  })
);

const useTriggerStyles = makeStyles((theme: Theme) =>
  createStyles({
    trigger: {
      minWidth: 86,
      height: 56,
      padding: '0 10px 0 12px',
      gap: 6,
      borderTopLeftRadius: 16,
      borderBottomLeftRadius: 16,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      backgroundColor: getBackground(theme.palette.type === 'dark').backgroundInput,
      transition: theme.transitions.create(['background-color', 'border-color']),
      border: '1px solid transparent',
      '&:hover, &.MuiButtonBase-root:focus-visible': {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-disabled': {
        opacity: 0.6,
        cursor: 'not-allowed',
      },
    },
    triggerOpen: {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.primary.main,
    },
    flag: {
      fontSize: 20,
      lineHeight: 1,
    },
    dialCode: {
      fontSize: 14,
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
    chevron: {
      fontSize: 16,
      color: theme.palette.text.secondary,
      transition: theme.transitions.create('transform'),
    },
    chevronOpen: {
      transform: 'rotate(180deg)',
    },
  })
);

const usePopoverStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      width: 320,
      maxHeight: 360,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 12,
      overflow: 'hidden',
    },
    searchWrap: {
      padding: 12,
      borderBottom: `1px solid ${theme.palette.divider}`,
      flex: '0 0 auto',
    },
    list: {
      overflowY: 'auto',
      flex: '1 1 auto',
      padding: '4px 0',
    },
    item: {
      width: '100%',
      justifyContent: 'flex-start',
      padding: '8px 16px',
      textAlign: 'left',
      gap: 10,
      '&:hover, &.MuiButtonBase-root:focus-visible': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    itemSelected: {
      backgroundColor: theme.palette.action.selected,
    },
    itemFlag: {
      fontSize: 20,
      lineHeight: 1,
      flex: '0 0 auto',
    },
    itemName: {
      flex: '1 1 auto',
      fontSize: 14,
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    itemDial: {
      flex: '0 0 auto',
      fontSize: 13,
      color: theme.palette.text.secondary,
    },
    sectionLabel: {
      padding: '6px 16px 4px',
      fontSize: 11,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
    },
    empty: {
      padding: '16px',
      textAlign: 'center',
      color: theme.palette.text.secondary,
      fontSize: 14,
    },
  })
);

const matchesQuery = (c: PhoneCountry, q: string) => {
  if (!q) return true;
  const normalized = q.trim().toLowerCase();
  if (!normalized) return true;
  return (
    c.name.toLowerCase().includes(normalized) ||
    c.code.toLowerCase().includes(normalized) ||
    c.dialCode.toLowerCase().includes(normalized) ||
    c.dialCode.replace('+', '').includes(normalized.replace('+', ''))
  );
};

export interface PhoneNumberFieldV2Props extends FieldProps {
  /**
   * Called when a paste holds 2+ numbers, with the country currently picked in the field so
   * bare numbers can be resolved. Return true to swallow the paste instead of letting the
   * field mangle the list into a single number.
   */
  onPasteList?: (text: string, defaultCountryCode: string) => boolean;
}

export const PhoneNumberFieldV2: React.FC<PhoneNumberFieldV2Props> = ({
  margin,
  label = 'Phone Number',
  name = '',
  rules = mobileNumberRules,
  autoFocus = false,
  disabled = false,
  onKeyDown,
  onPasteList,
}) => {
  const { control } = useFormContext();
  const [pickedCountry, setPickedCountry] = useState<PhoneCountry | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');

  const inputClasses = useInputStyles();
  const labelClasses = useLabelStyles();
  const triggerClasses = useTriggerStyles();
  const popoverClasses = usePopoverStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const pendingCursorRef = useRef<number | null>(null);
  const countryRef = useRef<PhoneCountry>(countries[0]);

  // Validate rule is stable; it reads the live country via ref so RHF doesn't
  // tear down the controller on every country change.
  const validate = useCallback(
    (value: string) => validatePhoneByCountry(value, countryRef.current),
    []
  );
  const controllerRules = useMemo(() => ({ ...rules, validate }), [rules, validate]);
  const { field, fieldState } = useController({ control, name, rules: controllerRules });

  // Derive country from the user's explicit pick, falling back to detection from
  // the current form value. Keeps display and country in sync in a single render.
  const formValue = String(field.value || '');
  const country =
    pickedCountry || detectCountryByDigits(stripNonDigits(formValue)) || countries[0];
  countryRef.current = country;

  const formLocalDigits = formValue ? extractLocalDigits(stripNonDigits(formValue), country) : '';
  const displayValue = formatDisplay(country, formLocalDigits);

  // Restore caret after every render driven by our edits.
  useLayoutEffect(() => {
    if (pendingCursorRef.current !== null && inputRef.current) {
      const pos = pendingCursorRef.current;
      pendingCursorRef.current = null;
      inputRef.current.setSelectionRange(pos, pos);
    }
  });

  const { priorityMatches, restMatches } = useMemo(() => {
    const priority = countries.slice(0, PRIORITY_COUNT);
    const rest = countries.slice(PRIORITY_COUNT);
    return {
      priorityMatches: priority.filter(c => matchesQuery(c, query)),
      restMatches: rest.filter(c => matchesQuery(c, query)),
    };
  }, [query]);

  const totalMatches = priorityMatches.length + restMatches.length;
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setQuery('');
  };

  const handleSelectCountry = (next: PhoneCountry) => {
    handleClose();
    const nextLocal = applyCountryRules(next, formLocalDigits).slice(0, splitMask(next).slots);
    const nextRaw = nextLocal ? next.dialCode + nextLocal : '';
    setPickedCountry(next);
    if (nextRaw !== formValue) field.onChange(nextRaw);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const selStart = event.target.selectionStart ?? raw.length;
    const allDigits = stripNonDigits(raw);

    // Auto-detect country only when the user explicitly enters an international prefix
    // (typed/pasted starting with `+`). Otherwise short single digits like `7` would
    // hijack the country selector (`+7` Russia, `+1` US, etc).
    let nextCountry = country;
    if (raw.trim().startsWith('+')) {
      const detected = detectCountryByDigits(allDigits);
      if (detected && detected.code !== country.code) {
        nextCountry = detected;
      }
    }

    let nextLocal = extractLocalDigits(allDigits, nextCountry);
    nextLocal = applyCountryRules(nextCountry, nextLocal);

    const truncated = nextLocal.slice(0, splitMask(nextCountry).slots);
    const localBefore = Math.min(
      localDigitsBeforeCursor(raw, selStart, nextCountry),
      truncated.length
    );

    const nextRaw = truncated ? nextCountry.dialCode + truncated : '';
    const nextDisplay = formatDisplay(nextCountry, truncated);
    const cursor = positionAtLocalDigit(nextDisplay, nextCountry, localBefore);

    const formChanged = nextRaw !== formValue;
    const countryChanged = nextCountry.code !== country.code;

    if (countryChanged) setPickedCountry(nextCountry);
    if (formChanged) field.onChange(nextRaw);

    if (formChanged || countryChanged) {
      // A re-render will run; let useLayoutEffect place the caret in the new DOM.
      pendingCursorRef.current = cursor;
    } else if (inputRef.current && raw !== nextDisplay) {
      // No re-render coming (value unchanged) but the DOM still has the user's raw
      // text — sync it to the formatted form and place the caret directly.
      inputRef.current.value = nextDisplay;
      inputRef.current.setSelectionRange(cursor, cursor);
    }
  };

  // `<input type="text">` strips CR/LF, so by the time handleInputChange runs a pasted
  // spreadsheet column has already collapsed into one long digit string. The paste event is
  // the only place the separators still exist.
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (!onPasteList || disabled) return;
    const text = event.clipboardData?.getData('text') ?? '';
    // A single number — including one copied from a spreadsheet cell with a trailing
    // newline — keeps the existing behaviour and lands in the field.
    if (splitPhoneTokens(text).length < 2) return;
    if (onPasteList(text, country.code)) {
      event.preventDefault();
    }
  };

  const renderItem = (c: PhoneCountry) => {
    const selected = c.code === country.code;
    return (
      <ButtonBase
        key={c.code}
        focusRipple
        onClick={() => handleSelectCountry(c)}
        className={`${popoverClasses.item} ${selected ? popoverClasses.itemSelected : ''}`}>
        <span className={popoverClasses.itemFlag}>{c.flag}</span>
        <span className={popoverClasses.itemName}>{c.name}</span>
        <span className={popoverClasses.itemDial}>{c.dialCode}</span>
      </ButtonBase>
    );
  };

  // Mirror MUI TextField's margin behaviour on the wrapper so the country trigger
  // and the input shift together vertically (otherwise the trigger floats above
  // the input when margin="normal" is passed in dialogs/forms).
  const marginStyle: React.CSSProperties =
    margin === 'normal'
      ? { marginTop: 16, marginBottom: 8 }
      : margin === 'dense'
      ? { marginTop: 8, marginBottom: 4 }
      : {};

  return (
    <Box display="flex" width="100%" style={marginStyle}>
      <ButtonBase
        focusRipple
        disabled={disabled}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Select country, currently ${country.name} ${country.dialCode}`}
        className={`${triggerClasses.trigger} ${open ? triggerClasses.triggerOpen : ''}`}>
        <span className={triggerClasses.flag}>{country.flag}</span>
        <span className={triggerClasses.dialCode}>{country.dialCode}</span>
        <FiChevronDown className={`${triggerClasses.chevron} ${open ? triggerClasses.chevronOpen : ''}`} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: popoverClasses.paper }}
        TransitionProps={{ onEntered: () => searchRef.current?.focus() }}>
        <div className={popoverClasses.searchWrap}>
          <TextField
            inputRef={searchRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search country or code"
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch />
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className={popoverClasses.list} role="listbox">
          {totalMatches === 0 && <div className={popoverClasses.empty}>No countries match "{query}"</div>}
          {priorityMatches.length > 0 && (
            <>
              {!query && <div className={popoverClasses.sectionLabel}>Suggested</div>}
              {priorityMatches.map(renderItem)}
              {restMatches.length > 0 && <Divider />}
            </>
          )}
          {restMatches.length > 0 && (
            <>
              {!query && <div className={popoverClasses.sectionLabel}>All countries</div>}
              {restMatches.map(renderItem)}
            </>
          )}
        </div>
      </Popover>

      <TextField
        variant="filled"
        label={label}
        value={displayValue}
        inputRef={inputRef}
        onChange={handleInputChange}
        onBlur={field.onBlur}
        error={!!fieldState.error?.message}
        helperText={fieldState.error?.message || ''}
        fullWidth
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        inputProps={{ inputMode: 'tel', autoComplete: 'tel', onPaste: handlePaste }}
        InputProps={
          {
            classes: inputClasses,
            disableUnderline: true,
            disabled: disabled,
          } as Partial<OutlinedInputProps>
        }
        InputLabelProps={{ classes: { root: labelClasses.label, focused: labelClasses.label } }}
      />
    </Box>
  );
};
